import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Checkbox, Empty, Form, FormInstance, Input, Typography } from 'antd'
import clsx from 'clsx'
import useGetAnnotationColor from 'hooks/useGetAnnotationColor'
import useSWR from 'swr'
import { AnnotationType, BuildingFile, ListBuildingFiles } from 'types/building'
import Validators from 'utils/validators'

import Button from 'components/atoms/Button'
import Modal from 'components/atoms/Modal'
import Spinner from 'components/atoms/Spinner'
import Title from 'components/atoms/Title'

import PreviewFile from '../PreviewFile'

type Props = {
  form: FormInstance
  selectedType: AnnotationType | undefined
  selectedTabIndex: number
  selectedTabs: number
  selectedCategory: string
  linkParse: any
  nameParse: any
  currentListFile: BuildingFile[]
  setCurrentListFile: Dispatch<SetStateAction<BuildingFile[]>>
}

export default function BCPSetAnnotationCategoryDetail({
  form,
  selectedType,
  selectedTabIndex,
  selectedTabs,
  selectedCategory,
  linkParse,
  nameParse,
  currentListFile,
  setCurrentListFile
}: Props) {
  const { id } = useParams()
  const { t } = useTranslation()

  const { getAnnotationBgColor } = useGetAnnotationColor()

  const annotationBgColor = useMemo(
    () => (selectedType ? getAnnotationBgColor(selectedType) : ''),
    [getAnnotationBgColor, selectedType]
  )

  const [isSelectingFiles, setIsSelectingFiles] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState<BuildingFile[]>([])

  const { data, isValidating } = useSWR<ListBuildingFiles>(
    selectedType && selectedTabs
      ? {
          url: 'v1/buildings/files',
          params: {
            buildingId: id,
            category: selectedTabs
          }
        }
      : null
  )

  const filteredTabFileList = useMemo(
    () =>
      currentListFile.filter(
        (item) => data?.files.files.find((file) => file.id === item.id)
      ),
    [currentListFile, data?.files.files]
  )

  const filteredSavedFiles = useMemo(() => {
    if (selectedCategory === 'video') {
      return filteredTabFileList.filter(
        (item) =>
          item.extension === 'mp4' ||
          item.extension === 'mov' ||
          item.extension === 'MOV'
      )
    }
    if (selectedCategory === 'image') {
      return filteredTabFileList.filter(
        (item) =>
          item.extension === 'jpg' ||
          item.extension === 'png' ||
          item.extension === 'jpeg'
      )
    }
    if (selectedCategory === 'pdf') {
      return filteredTabFileList.filter((item) => item.extension === 'pdf')
    }
    return []
  }, [filteredTabFileList, selectedCategory])

  const filteredSelectableFiles = useMemo(() => {
    if (selectedCategory === 'video') {
      return data?.files.files.filter(
        (item) =>
          item.extension === 'mp4' ||
          item.extension === 'mov' ||
          item.extension === 'MOV'
      )
    }
    if (selectedCategory === 'image') {
      return data?.files.files.filter(
        (item) =>
          item.extension === 'jpg' ||
          item.extension === 'png' ||
          item.extension === 'jpeg'
      )
    }
    if (selectedCategory === 'pdf') {
      return data?.files.files.filter((item) => item.extension === 'pdf')
    }
    return []
  }, [data?.files.files, selectedCategory])

  const handleSelectFile = (file: BuildingFile) => {
    if (selectedFiles.find((item) => item.id === file.id)) {
      setSelectedFiles((prev) => prev.filter((item) => item.id !== file.id))
      return
    }
    setSelectedFiles((prev) => [...prev, file])
  }

  const handleClose = () => {
    setIsSelectingFiles(false)
    setSelectedFiles(currentListFile || [])
  }

  const handleSubmit = () => {
    setIsSelectingFiles(false)
    setCurrentListFile(selectedFiles)
  }

  const link = useMemo(
    () =>
      linkParse?.find((item: any, index: any) => index === selectedTabIndex),
    [linkParse, selectedTabIndex]
  )

  const name = useMemo(
    () =>
      nameParse?.find((item: any, index: any) => index === selectedTabIndex),
    [nameParse, selectedTabIndex]
  )

  useEffect(() => {
    if (currentListFile) {
      setSelectedFiles(currentListFile)
    }
  }, [currentListFile])

  useEffect(() => {
    if (selectedCategory === 'link' && selectedTabs) {
      form.setFieldValue('linkName', name || '')
      form.setFieldValue('link', link || '')
    }
    form.validateFields(['link', 'linkName'])
  }, [form, link, name, selectedCategory, selectedTabs])

  if (selectedCategory === 'link') {
    return (
      <Form
        form={form}
        colon={false}
        labelCol={{ span: 5 }}
        labelAlign="left"
        wrapperCol={{ span: 19 }}
      >
        <div className="h-[300px] pt-9 pl-[31px] pr-[50px]">
          <Form.Item
            name="link"
            label={t('building.inputURLAnnotation')}
            rules={[{ validator: Validators.urlValidator(t) }]}
          >
            <Input placeholder="https://" className="h-[38px]" />
          </Form.Item>
          <Form.Item
            name="linkName"
            label={t('building.inputNameAnnotation')}
            rules={[
              {
                validator: Validators.keywordsAndLengthCustomValidator(
                  t,
                  500,
                  undefined,
                  true
                )
              }
            ]}
          >
            <Input
              placeholder={t('building.placeholder')}
              className="h-[38px]"
            />
          </Form.Item>
        </div>
      </Form>
    )
  }

  return (
    <>
      <div className="flex flex-col">
        <Spinner spinning={isValidating}>
          <div className="h-[300px] overflow-auto">
            {!isValidating && filteredSavedFiles?.length ? (
              filteredSavedFiles
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((item) => (
                  <div
                    key={item.id}
                    className="grid grid-cols-[auto_1fr] gap-5 items-center px-[10px] h-[60px]"
                  >
                    <PreviewFile file={item} size="default" />
                    <Typography.Text
                      className="text-base font-medium text-[#021120]"
                      ellipsis={{
                        tooltip: item.name
                      }}
                    >
                      {item.name}
                    </Typography.Text>
                  </div>
                ))
            ) : (
              <Empty className="mt-5" />
            )}
          </div>
        </Spinner>

        <Button
          sizing="w-[300px] h-[60px] z-10"
          className={`mx-auto ${annotationBgColor}`}
          onClick={() => setIsSelectingFiles(true)}
        >
          {t('building.selectFile')}
        </Button>
      </div>

      <Modal open={isSelectingFiles} width={900} className="paddingless-modal">
        <div className="pt-10 pb-5">
          <Title title={t(`translation.${selectedTabIndex + 1}`)} />

          <Spinner spinning={isValidating}>
            <div className="mx-7 my-[30px] h-[360px] overflow-y-auto">
              {Boolean(!isValidating && filteredSelectableFiles?.length) &&
                filteredSelectableFiles
                  ?.sort((a, b) => a.name.localeCompare(b.name))
                  ?.map((item, index) => (
                    <div
                      key={item.id}
                      className={clsx(
                        'h-[60px] grid grid-cols-[auto_auto_1fr] gap-5 items-center cursor-pointer',
                        'border-0 border-b border-solid border-[#EEEFF2]',
                        { 'border-t': index === 0 }
                      )}
                      onClick={() => handleSelectFile(item)}
                    >
                      <Checkbox
                        checked={Boolean(
                          selectedFiles.find((file) => file.id === item.id)
                        )}
                      />
                      <PreviewFile file={item} size="large" />
                      <Typography.Text
                        className="text-base font-medium text-[#021120]"
                        ellipsis={{
                          tooltip: item.name
                        }}
                      >
                        {item.name}
                      </Typography.Text>
                    </div>
                  ))}

              {Boolean(!isValidating && !filteredSelectableFiles?.length) && (
                <Empty className="mt-5" />
              )}
            </div>
          </Spinner>

          <div className="pt-5 flex gap-10 justify-center border-0 border-t border-solid border-[#EEEFF2]">
            <Button sizing="w-[140px] h-[50px]" onClick={handleClose}>
              {t('common.cancel')}
            </Button>
            <Button
              type="primary"
              sizing="w-[140px] h-[50px]"
              onClick={handleSubmit}
            >
              {t('common.confirm')}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  )
}
